// components/customFunctionalComponents/components/TablaEscalamientos.jsx
import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useRef } from 'react';
import { Table, Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus, faCircleMinus, faEdit, faSave, faTimes } from "@fortawesome/free-solid-svg-icons"; // Importar iconos de FontAwesome
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

export const TablaEscalamientos = forwardRef (({ initialEscalamientos = [], protocoloId, onPostEscalamiento, onDeleteEscalamiento }, ref) => {
    // Log para ver la prop 'protocoloId' en cada render
    // console.log("[TablaEscalamientos - Render] protocoloId prop recibida:", protocoloId);

    // Definimos los campos del nuevo escalamiento usando los nombres esperados por el backend
    const [newEscalamiento, setNewEscalamiento] = useState({
        nivel: '',
        nombre_completo: '',
        telefono: '',
        correo: '',
        turno: '',
        puesto: ''
    });

    // Estado interno para la lista de escalamientos.
    const [escalamientos, setEscalamientos] = useState([]);

    // Referencias para las funciones de API pasadas por props (onPostEscalamiento ya no se usa directamente aquí)
    const onDeleteEscalamientoRef = useRef(onDeleteEscalamiento);

    // Referencia para el estado actual de 'escalamientos' para usar en callbacks
    const escalamientosRef = useRef(escalamientos);

    // Actualizar las referencias si las funciones o el estado cambian
    useEffect(() => {
        onDeleteEscalamientoRef.current = onDeleteEscalamiento;
    }, [onDeleteEscalamiento]);

    // Sincronizar escalamientosRef.current con el estado actual de 'escalamientos'
    useEffect(() => {
        escalamientosRef.current = escalamientos;
    }, [escalamientos]);


    // Sincronización del estado interno con la prop `initialEscalamientos`
    useEffect(() => {
        // console.log("[TablaEscalamientos - useEffect] initialEscalamientos recibido al sincronizar:", initialEscalamientos);
        // Asegurarse de que initialEscalamientos sea un array antes de mapear
        if (initialEscalamientos && Array.isArray(initialEscalamientos)) {
            const mappedEscalamientos = initialEscalamientos.map((item, index) => ({
                id: item.escalamiento_id || `TEMP_${Date.now()}-${index}-${Math.random().toString(36).substr(2, 5)}`, // Usamos escalamiento_id si existe, si no un TEMP_ID único
                escalamiento_id: item.escalamiento_id, // Guardamos el ID del backend
                nivel: item.nivel || '',
                nombre_completo: item.nombre_completo || '',
                telefono: item.telefono || '',
                correo: item.correo || '',
                turno: item.turno || '',
                puesto: item.puesto || ''
            }));
            setEscalamientos(mappedEscalamientos);
        } else {
            setEscalamientos([]); // Si no hay datos o no es un array, inicializar vacío
        }
    }, [initialEscalamientos]);

    // Métodos Imperativos expuestos al componente padre
    useImperativeHandle(ref, () => ({
        // Esta función ahora es CRÍTICA para que el padre obtenga los escalamientos locales
        getEscalamientosData: () => {
            return escalamientos.map(esc => ({
                // Incluimos el 'id' local para que el padre pueda filtrar por TEMP_ID
                id: esc.id,
                escalamiento_id: String(esc.id).startsWith('TEMP_') ? null : esc.escalamiento_id,
                nivel: Number(esc.nivel), // Asegurar que el nivel sea número
                nombre_completo: esc.nombre_completo,
                telefono: esc.telefono,
                correo: esc.correo,
                turno: esc.turno,
                puesto: esc.puesto,
            }));
        },

        resetEscalamientos: () => {
            // console.log("[TablaEscalamientos] Reseteando escalamientos y formulario.");
            setEscalamientos([]);
            setNewEscalamiento({
                nivel: '',
                nombre_completo: '',
                telefono: '',
                correo: '',
                turno: '',
                puesto: ''
            });
        }
    }));

    // Maneja los cambios en los inputs de añadir y editar
    const handleInputChange = (e, id, field) => {
        const { value } = e.target;
        let newValue = value;

        // Validación para el campo de teléfono: solo números
        if (field === 'telefono') {
            newValue = value.replace(/\D/g, ''); // Elimina cualquier caracter que no sea un dígito
        }

        if (id === 'new') {
            setNewEscalamiento(prev => ({
                ...prev,
                [field]: newValue
            }));
        } else {
            setEscalamientos(prevEscalamientos =>
                prevEscalamientos.map(esc =>
                    esc.id === id ? { ...esc, [field]: newValue } : esc
                )
            );
        }
    };

    // Añadir un nuevo escalamiento: AHORA SOLO ADICIÓN VISUAL (PSEUDO-POST)
    const handleAddEscalamiento = useCallback(async () => {
        // console.log("[TablaEscalamientos] handleAddEscalamiento - Iniciando adición.");
        // console.log("[TablaEscalamientos] newEscalamiento actual (antes de añadir):", newEscalamiento);

        // Validaciones básicas
        if (!newEscalamiento.nivel.trim() || !newEscalamiento.nombre_completo.trim() || !newEscalamiento.telefono.trim()) {
            toast.error("Nivel, Nombre Completo y Teléfono son obligatorios.");
            return;
        }
        if (isNaN(Number(newEscalamiento.nivel))) {
            toast.error("El nivel debe ser un número.");
            return;
        }
        if (newEscalamiento.telefono.trim().length !== 10) {
            toast.error ("El teléfono debe tener 10 dígitos");
            return
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (newEscalamiento.correo.trim() !== '' && !emailRegex.test(newEscalamiento.correo.trim())){
            toast.error("El formato del correo debe ser valido ej: prueba@ejemplo.com");
            return;
        }

        // --- Validación Frontend de Duplicados ---
        const currentEscalamientosInTable = escalamientosRef.current; // Usar la referencia más reciente
        const isDuplicateNivel = currentEscalamientosInTable.some(esc => Number(esc.nivel) === Number(newEscalamiento.nivel)); // Comparar como número
        const isDuplicateNombre = currentEscalamientosInTable.some(esc => esc.nombre_completo === newEscalamiento.nombre_completo.trim());
        const isDuplicateCorreo = newEscalamiento.correo.trim() !== '' && currentEscalamientosInTable.some(esc => esc.correo === newEscalamiento.correo.trim());
        const isDuplicateTelefono = newEscalamiento.telefono.trim() !== '' && currentEscalamientosInTable.some(esc => esc.telefono === newEscalamiento.telefono.trim());


        if (isDuplicateNivel || isDuplicateNombre || isDuplicateCorreo || isDuplicateTelefono) {
            let errorMessage = 'No se puede agregar el escalamiento. Ya existe un escalamiento con el mismo:';
            if (isDuplicateNivel) errorMessage += '\n- Nivel';
            if (isDuplicateNombre) errorMessage += '\n- Nombre Completo';
            if (isDuplicateCorreo) errorMessage += '\n- Correo';
            if (isDuplicateTelefono) errorMessage += '\n- Teléfono';

            Swal.fire({
                icon: 'error',
                title: 'Duplicado Detectado',
                text: errorMessage,
                confirmButtonText: 'Entendido'
            });
            return;
        }
        // --- Fin Validación Frontend ---

        const escalamientoToAdd = {
            nivel: Number(newEscalamiento.nivel), // Asegurar que el nivel sea un número
            nombre_completo: newEscalamiento.nombre_completo.trim(),
            telefono: newEscalamiento.telefono.trim(),
            correo: newEscalamiento.correo.trim(),
            turno: newEscalamiento.turno.trim(),
            puesto: newEscalamiento.puesto.trim(),
        };

        // Solo adición visual. El guardado real se hará al guardar el protocolo principal.
        const tempId = `TEMP_${Date.now()}-${Math.random().toString(36).substr(2, 5)}`; // Generar un ID temporal más robusto
        setEscalamientos(prevEscalamientos => {
            const updatedEscalamientos = [
                ...prevEscalamientos,
                { ...escalamientoToAdd, id: tempId, escalamiento_id: null } // Añadir con ID temporal y escalamiento_id nulo
            ];
            // console.log("[TablaEscalamientos] Nuevo estado de 'escalamientos' DESPUÉS de pseudo-POST:", updatedEscalamientos);
            return updatedEscalamientos;
        });
        // toast.info('Escalamiento añadido localmente. Se guardará al guardar el protocolo principal.');

        // Limpiar el formulario de adición
        setNewEscalamiento({
            nivel: '', nombre_completo: '', telefono: '',
            correo: '', turno: '', puesto: ''
        });
        // console.log("[TablaEscalamientos] Formulario de nuevo escalamiento reseteado.");

    }, [newEscalamiento, escalamientosRef]); // Eliminadas dependencias relacionadas con API y protocoloId

    // Eliminar un escalamiento (con llamada a API DELETE si es necesario)
    const handleDeleteEscalamiento = useCallback(async (idToDelete) => {
        const currentEscalamientos = escalamientosRef.current;
        const escalamientoToRemove = currentEscalamientos.find(esc => esc.id === idToDelete);

        if (!escalamientoToRemove) {
            toast.error("Escalamiento no encontrado.");
            return;
        }

        // Confirmación con SweetAlert2
        const resultSwal = await Swal.fire({
            title: '¿Estás seguro?',
            text: `Vas a eliminar el escalamiento de ${escalamientoToRemove.nombre_completo}. ¡Esta acción no se puede deshacer!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (!resultSwal.isConfirmed) {
            return; // El usuario canceló la eliminación
        }

        if (escalamientoToRemove.escalamiento_id) {
            const deleteData = escalamientoToRemove.escalamiento_id; // La API espera solo el escalamiento_id en la URL
            // console.log("[TablaEscalamientos] Enviando DELETE de escalamiento con ID:", deleteData);

            try {
                const result = await onDeleteEscalamientoRef.current(deleteData);
                // console.log("[TablaEscalamientos] Resultado del DELETE de escalamiento:", result);

                if (result.ok) {
                    // toast.success(result.msg || "Escalamiento eliminado con éxito!");
                    setEscalamientos(prevEscalamientos =>
                        prevEscalamientos.filter(esc => esc.id !== idToDelete)
                    );
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error al eliminar',
                        text: result.msg || "Ocurrió un error al eliminar el escalamiento del servidor.",
                        confirmButtonText: 'Entendido'
                    });
                }
            } catch (error) {
                console.error("Error al eliminar escalamiento:", error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error inesperado',
                    text: "Ocurrió un error inesperado al eliminar el escalamiento.",
                    confirmButtonText: 'Entendido'
                });
            }
        } else {
            // Si es un escalamiento nuevo (con ID temporal), solo eliminar visualmente
            toast.warn("Escalamiento eliminado.");
            setEscalamientos(prevEscalamientos =>
                prevEscalamientos.filter(esc => esc.id !== idToDelete)
            );
        }
    }, [onDeleteEscalamientoRef, escalamientosRef]);


    // La edición de escalamientos no está implementada en la API (no hay PUT para escalamientos)
    // por lo que los botones de edición solo se muestran, pero no tienen funcionalidad de guardado.
    // Se mantiene la estructura para una posible futura implementación.
    const handleEditClick = (escalamiento) => {
        // Si se implementara la edición, aquí cargarías los datos en un formulario de edición
        toast.info("La edición de escalamientos no está disponible. Solo se puede agregar y eliminar.");
    };

    // El botón de agregar escalamiento ahora SIEMPRE está habilitado (siempre que el modal esté abierto)
    // ya que el guardado es local hasta que se guarda el protocolo principal.
    const isAddButtonDisabled = false; // Ya no depende de protocoloId

    return (
        <div className="mt-4">
            <label className="mb-3">
                Escalamiento
            </label>
            <Table hover responsive size="xs">
                <thead>
                    <tr>
                        <th>Nivel</th>
                        <th>Nombre Completo</th>
                        <th>Teléfono</th>
                        <th>Correo</th>
                        <th>Turno</th>
                        <th>Puesto</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {/* Fila para agregar nuevos escalamientos */}
                    <tr>
                        <td className='align-content-center'>
                            <input
                                type="number"
                                className='inputMinimalista'
                                placeholder="Nivel"
                                value={newEscalamiento.nivel || ''}
                                onChange={(e) => handleInputChange(e, 'new', 'nivel')}
                            />
                        </td>
                        <td>
                            <input
                                type="text"
                                className='inputMinimalista'
                                placeholder="Nombre Completo"
                                value={newEscalamiento.nombre_completo || ''}
                                onChange={(e) => handleInputChange(e, 'new', 'nombre_completo')}
                            />
                        </td>
                        <td className='align-content-center'>
                            <input
                                type="tel"
                                className='inputMinimalista'
                                placeholder="Número/Teléfono"
                                value={newEscalamiento.telefono || ''}
                                onChange={(e) => handleInputChange(e, 'new', 'telefono')}
                                maxLength={10}

                            />
                        </td>
                        <td className='align-content-center'>
                            <input
                                type="email"
                                className='inputMinimalista'
                                placeholder="Correo"
                                value={newEscalamiento.correo || ''}
                                onChange={(e) => handleInputChange(e, 'new', 'correo')}
                            />
                        </td>
                        <td>
                            <input
                                type="text"
                                className='inputMinimalista'
                                placeholder="Turno"
                                value={newEscalamiento.turno || ''}
                                onChange={(e) => handleInputChange(e, 'new', 'turno')}
                            />
                        </td>
                        <td>
                            <input
                                type="text"
                                className='inputMinimalista'
                                placeholder="Puesto"
                                value={newEscalamiento.puesto || ''}
                                onChange={(e) => handleInputChange(e, 'new', 'puesto')}
                            />
                        </td>
                        <td className='align-content-center text-center fa-2x'>
                            <FontAwesomeIcon
                                icon={faCirclePlus}
                                className={`text-primary ${isAddButtonDisabled ? 'text-muted' : ''}`} // Cambiar color si está deshabilitado
                                style={{ cursor: isAddButtonDisabled ? 'not-allowed' : 'pointer'}}
                                onClick={isAddButtonDisabled ? null : handleAddEscalamiento} // Deshabilitar click si el botón está deshabilitado
                                title={isAddButtonDisabled ? "Guarde el protocolo principal para agregar escalamientos" : "Agregar Escalamiento"}
                            />
                        </td>
                    </tr>
                    {/* Filas de escalamientos existentes */}
                    {escalamientos.map((escalamiento) => (
                        <tr key={escalamiento.id}>
                            <td className='align-content-center'>
                                <input
                                    type="number"
                                    className='inputMinimalista'
                                    value={escalamiento.nivel || ''}
                                    onChange={(e) => handleInputChange(e, escalamiento.id, 'nivel')}
                                />
                            </td>
                            <td>
                                <input
                                    type="text"
                                    className='inputMinimalista'
                                    value={escalamiento.nombre_completo || ''}
                                    onChange={(e) => handleInputChange(e, escalamiento.id, 'nombre_completo')}
                                />
                            </td>
                            <td className='align-content-center'>
                                <input
                                    type="number"
                                    className='inputMinimalista'
                                    value={escalamiento.telefono || ''}
                                    onChange={(e) => handleInputChange(e, escalamiento.id, 'telefono')}
                                />
                            </td>
                            <td className='align-content-center'>
                                <input
                                    type="email"
                                    value={escalamiento.correo || ''}
                                    className='inputMinimalista'
                                    onChange={(e) => handleInputChange(e, escalamiento.id, 'correo')}
                                />
                            </td>
                            <td>
                                <input
                                    type="text"
                                    className='inputMinimalista'
                                    value={escalamiento.turno || ''}
                                    onChange={(e) => handleInputChange(e, escalamiento.id, 'turno')}
                                />
                            </td>
                            <td>
                                <input
                                    type="text"
                                    className='inputMinimalista'
                                    value={escalamiento.puesto || ''}
                                    onChange={(e) => handleInputChange(e, escalamiento.id, 'puesto')}
                                />
                            </td>
                            <td className='align-content-center text-center fa-2x'>
                                {/* Botón de edición - solo muestra info, no guarda */}
                                {/* <FontAwesomeIcon
                                    icon={faEdit}
                                    className="text-info me-2"
                                    style={{ cursor: 'pointer'}}
                                    onClick={() => handleEditClick(escalamiento)}
                                    title="Editar Escalamiento (solo info)"
                                /> */}
                                <FontAwesomeIcon
                                    icon={faCircleMinus}
                                    className="text-danger"
                                    style={{ cursor: 'pointer'}}
                                    onClick={() => handleDeleteEscalamiento(escalamiento.id)}
                                    title="Eliminar Escalamiento"
                                />
                            </td>
                        </tr>
                    ))}
                    {escalamientos.length === 0 && newEscalamiento.nombre_completo.trim() === '' && (
                        <tr>
                            <td colSpan="7" className="text-center text-muted">
                                No hay escalamientos. Agregue uno usando la fila de arriba.
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    )
});
